import type { Component, EventEmitter } from '@angular/core';
import { formatDate } from '@angular/common';
import type { CellKeyDownEvent, ColDef, ColumnState, DataTypeDefinition, EditableCallbackParams, ExcelStyle, FullWidthCellKeyDownEvent, GridApi, ICellEditorParams, ICellRendererParams, IHeaderParams, ILargeTextEditorParams, IMultiFilterParams, INumberFilterParams, IRichCellEditorParams, IRowNode, SetFilterParams, ValueFormatterParams, ValueGetterParams, ValueSetterParams } from '@ag-grid-community/core';
import { DATE_FORMATS } from '@app/core/models/date-adapter';
import { DateTimeCellEditorComponent } from '../cell-editors/date-time-cell-editor/date-time-cell-editor.component';
import { DateCellEditorComponent } from '../cell-editors/date-cell-editor/date-cell-editor.component';
import { LookupCellEditorComponent } from '../cell-editors/lookup-cell-editor/lookup-cell-editor.component';
import { LookupMultiCellEditorComponent } from '../cell-editors/lookup-multi-cell-editor/lookup-multi-cell-editor.component';
import { NumberCellEditorComponent, NumberCellEditorParams } from '../cell-editors/number-cell-editor/number-cell-editor.component';
import { CheckboxColumnHeaderComponent } from '../column-headers/checkbox-column-header/checkbox-column-header.component';
import { ChipCellRendererComponent } from '../cell-renderers/chip-cell-renderer/chip-cell-renderer.component';
import { GridFunctionsCellRendererComponent, type DatataskEvent, type GridFunctionsCellRendererComponentParams, type ModalEvent, type NavigateEvent } from '../cell-renderers/grid-functions-cell-renderer/grid-functions-cell-renderer.component';
import { LinkCellRendererComponent, type LinkCellRendererParams } from '../cell-renderers/link-cell-renderer/link-cell-renderer.component';
import { SymbolCellRendererComponent, type SymbolCellRendererParams } from '../cell-renderers/symbol-cell-renderer/symbol-cell-renderer.component';
import { ThumbnailCellRendererComponent } from '../cell-renderers/thumbnail-cell-renderer/thumbnail-cell-renderer.component';
import { DateFloatingFilterComponent } from '../floating-filters/date-floating-filter/date-floating-filter.component';
import { CheckboxFloatingFilterComponent } from '../floating-filters/checkbox-floating-filter/checkbox-floating-filter.component';
import { ChipFloatingFilterComponent } from '../floating-filters/chip-floating-filter/chip-floating-filter.component';
import { ChipFilterComponent } from '../filters/chip-filter/chip-filter.component';
import { GridModalRegistry } from '../grid.decorators';
import { GridHtmlTooltipComponent } from '../tooltips/grid-html-tooltip/grid-html-tooltip.component';
import type { DropdownConfig, GridColumn, GridOptions, GridRow, LookupCellEditorParams, LookupConfig } from '../grid.types';
import { getCellDataType, isCheckboxColumn, isChipsColumn, isDateColumn, isDateTimeColumn, isEditableDropdown, isEditableLookup, isEditableMultiLookup, isLongTextColumn, isNumberColumn } from './grid.helpers';
import { GridComponent } from '../grid.component';

export interface GridColumnHelperContext<TType extends GridRow = GridRow> {
    options: GridOptions<TType>;
    ttOptions: GridOptions<TType>;
    translations: { [key: string]: string };
    defaultColumnDefinitions: ColDef;
    excelStyles: ExcelStyle[];
    getGridApi: () => GridApi | undefined;
    datatask: { Post(method: number, parameters: any): Promise<any[]> };
    rowSelection: 'single' | 'multiple' | undefined;
    grid: GridComponent<TType>;
    ttOpenModal: EventEmitter<ModalEvent>;
    ttNavigate: EventEmitter<NavigateEvent>;
    ttDatatask: EventEmitter<DatataskEvent>;
    valueSetter: (params: ValueSetterParams, column: GridColumn) => boolean;
    getCellSaveHighlightState?: (rowId: string | null | undefined, colId: string | null | undefined) => 'pending' | 'saved' | null;
    onCellEditorValueChanged?: () => void;
    onCellKeyDown: (event: CellKeyDownEvent | FullWidthCellKeyDownEvent | null, keyboardEvent?: KeyboardEvent, colname?: string, rowIndex?: null | number) => unknown;
    isEditingLocked?: () => boolean;
}

type EditableScriptFunction = (row: GridRow, column: GridColumn, params?: EditableCallbackParams) => unknown;

const editableScriptCache = new WeakMap<GridColumn, { script: string; fn: EditableScriptFunction }>();

export function createGridColumnTypes(): { [key: string]: ColDef } {
    return {
        centerAligned: {
            cellClass: 'tt-grid__cell--center',
            headerClass: 'tt-grid__cell--center',
        },
    };
}

export function createDefaultColumnDefinitions({ showHeaderMenuButton, sortable }: { showHeaderMenuButton: boolean; sortable?: boolean }): ColDef {
    return {
        minWidth: 80,
        enableValue: true,
        enableRowGroup: true,
        enablePivot: true,
        suppressHeaderMenuButton: !showHeaderMenuButton,
        sortable: sortable,
        autoHeight: true,
        autoHeaderHeight: true,
        wrapText: false,
        filter: true,
        floatingFilter: true,
        contextMenuItems: ['copy', 'copyWithHeaders', 'copyWithGroupHeaders', 'paste'],
        pivotComparator: (valueA, valueB) => valueA.localeCompare(valueB),
    };
}

export function createAutoGroupColumnDef(): ColDef {
    return {
        filter: 'agGroupColumnFilter',
        cellRendererParams: { suppressCount: true },
        pinned: 'left',
    };
}

export function createDataTypeDefinitions(): { [cellDataType: string]: DataTypeDefinition } {
    return {
        datetime: {
            baseDataType: 'date',
            extendsDataType: 'date',
        },
    };
}

export function preserveCurrentColumnStateInColDefs(columnDefinitions: ColDef[], columnState?: ColumnState[], preserveSort = true): ColDef[] {
    if (!columnState?.length && preserveSort) return columnDefinitions;

    const columnStateById = new Map(columnState?.map((state) => [state.colId, state]) ?? []);

    return columnDefinitions.map((columnDefinition) => {
        const colId = columnDefinition.colId ?? columnDefinition.field;
        const schemaSort = preserveSort ? {} : { sort: columnDefinition.sort ?? null, sortIndex: columnDefinition.sort ? (columnDefinition.sortIndex ?? null) : null };

        if (!colId) return { ...columnDefinition, ...schemaSort };

        const state = columnStateById.get(colId);

        if (!state) return { ...columnDefinition, ...schemaSort };

        return {
            ...columnDefinition,
            width: state.width,
            hide: state.hide ?? undefined,
            pinned: state.pinned,
            sort: preserveSort ? state.sort : (columnDefinition.sort ?? null),
            sortIndex: preserveSort ? (state.sortIndex ?? undefined) : columnDefinition.sort ? (columnDefinition.sortIndex ?? null) : null,
            aggFunc: state.aggFunc,
            pivot: state.pivot ?? undefined,
            pivotIndex: state.pivotIndex ?? undefined,
            rowGroup: state.rowGroup ?? undefined,
            rowGroupIndex: state.rowGroupIndex ?? undefined,
        };
    });
}

/**
 * Maps the given list of grid columns to AG-grid ColDef.
 *
 * @param columns the list of columns to map to AG-grid ColDef.
 * @returns a list of objects that can be used for AG-Grid ColDef.
 */
export function mapGridColumnsToColDefs<TType extends GridRow = GridRow>(columns: GridColumn[], context: GridColumnHelperContext<TType>): ColDef[] {
    const allColumns: ColDef[] = [];

    context.options.data!.columnInfo = {};
    columns.forEach((column: GridColumn) => {
        const editColumn = context.options.config?.editColumns?.find((editCol) => editCol.key === column.colname);

        context.options.data!.columnInfo![column.colname] = {
            colname: column.colname,
            from: column.colname,
            title: column.title,
            remember: !!column.title,
            decimalWidth: column.width,
            type: getCellDataType(column),
            editType: column.edittype,
            coltype: column.coltype,
            dd_data: column.dd_data,
            dd_data_id: column.dd_data_id,
            dd_data_name: column.dd_data_name,
            editable: column.editable || (context.ttOptions.config?.editColumns?.some((item) => item.key === column.colname) ?? false),
            editableScript: column.editable_script,
            base64: column.base64,
            clickonly: editColumn?.clickonly || (column.editable as LookupConfig)?.clickonly || (isLongTextColumn(column) && column.editable === true && context.options.config?.navigation?.altNav),
        };
    });

    context.options.data!.hasGridGlyphs = false;
    context.options.data!.hasGotoParms = false;
    context.options.data!.hasThumbnails = false;
    context.options.data!.hasSelection = false; //TODO: or not todo, that is the question. ('cause what if we want a selection column without the default behaviour of ag-grid?, anyway not implemented)

    let colDefs = columns
        .sort((a, b) => {
            const group = (n: number | null) => (n === null ? 0 : n < 1e4 ? -1 : 1);

            const groupA = group(a.colorder);
            const groupB = group(b.colorder);

            if (groupA !== groupB) return groupA - groupB;

            if (a.colorder === null || b.colorder === null) return 0;

            return a.colorder - b.colorder;
        })
        .map((column) => {
            if (column.colname === 'item_glyphicon' || column.colname === 'item_glyphicon_color') {
                context.options.data!.hasGridGlyphs = true;
                return null;
            }

            if (column.colname === 'item_state' || column.colname === 'item_parms' || column.colname === 'item_path') {
                context.options.data!.hasGotoParms = true;
                return null;
            }

            if (column.colname === 'item_thumb') {
                context.options.data!.hasThumbnails = true;
            }

            if (column.visibility === 'none' || column.colname === '_uuid') {
                return null;
            }

            return createColDefFromGridColumn(column, context);
        })
        .filter((column) => column !== null) as ColDef[];

    if (context.options.kendo?.selectable === 'multiple' || context.options.data?.hasSelection) {
        allColumns.push(createCheckboxSelectionColumn(context));
    }

    if (context.options.data!.hasGridGlyphs) {
        allColumns.push(createSymbolColumn(context));
    }

    if (context.options.data!.hasGotoParms || context.options.data!.hasSpecialFuncEdit || context.options.config?.specialFunc?.duplicate || context.options.config?.specialFunc?.buttons?.some((button) => !button.type || (button.type !== 'noclick' && button.type !== 'cell')) || (context.options.config?.specialFunc?.groupButtons?.length ?? 0) > 0) {
        allColumns.push(createGridFunctionsColumn(context));
    }

    allColumns.push(...colDefs);

    return allColumns;
}

/**
 * Creates a column definition from the given grid column.
 *
 * @param column the grid column object to create a column definition for.
 * @returns the column defintiion based on the given grid column.
 */
function createColDefFromGridColumn<TType extends GridRow = GridRow>(column: GridColumn, context: GridColumnHelperContext<TType>): ColDef {
    const tooltipComponent = getTooltipComponent(column);

    return <ColDef>{
        colId: column.colname,
        field: column.colname,
        headerName: column.title || '',
        headerClass: (_) => getHeaderClasses(column, context),
        cellStyle: (params) => getColumnCellStyle(column, params, context),
        cellClass: (params) => getCellClass(column, params, context),
        cellClassRules: getCellClassRules(column, context),
        width: !!column.colwidth ? column.colwidth : null,
        type: getColumnTypes(column),
        filter: isGridColumnFilterable(context.options, column) ? getFilterComponent(column, context) : false,
        sort: column?.sort || undefined,
        sortIndex: column.sort_index === null ? undefined : column.sort_index,
        // floatingFilter: true,
        floatingFilterComponent: isGridColumnFilterable(context.options, column) ? getColumnFloatingFilterType(column) : false,
        filterParams: isGridColumnFilterable(context.options, column) ? getFilterParams(column, context) : undefined,
        headerComponent: getHeaderComponent(column, context),
        headerComponentParams: (params: IHeaderParams) => getHeaderComponentParams(params, column, context),
        suppressFloatingFilterButton: !isGridColumnFilterable(context.options, column) || isCheckboxColumn(column),
        cellRenderer: getCellRenderer(column, context),
        cellRendererParams: (params: ICellRendererParams) => getCellRendererParams(params, column, context),
        cellEditor: getCellEditor(column),
        cellEditorParams: (params: ICellEditorParams) => getCellEditorParams(column, params, context),
        hide: !column.visibility?.toLocaleLowerCase().includes('visible'),
        lockVisible: setLockVisible(column, context),
        cellDataType: getCellDataType(column),
        suppressNavigable: (event: any) => context.getGridApi()?.getPinnedBottomRow(0)?.data._uuid === event.node.data._uuid,
        cellEditorPopupPosition: isEditableMultiLookup(column.editable) ? 'over' : 'under',
        editable: (event: EditableCallbackParams) => isEditable(column, context, event),
        sortable: !!column.sortable ? column.sortable !== '0' : undefined,
        valueFormatter: (params: ValueFormatterParams) => formatGridColumnValue(params, column),
        valueGetter: (params: ValueGetterParams) => getGridColumnValue(params, column),
        valueSetter: (params: ValueSetterParams) => context.valueSetter(params, column),
        tooltipField: !!tooltipComponent ? column.colname : undefined,
        tooltipComponent: tooltipComponent,
        tooltipComponentParams: (params: any) => ({ ...params, tt_column: column }),
        headerTooltip: column.header_tooltip,
        pivot: column.pivot ?? false,
        pivotIndex: column.pivotindex,
        rowGroup: column.rowgroup ?? false,
        rowGroupIndex: column.rowgroupindex,
        rowDrag: column.rowdrag ?? false,
        aggFunc: column.aggfunc,
        pinned: column.pinned,
        //      valueParser: isEditableDropdown(column.editable) ? (params: ValueParserParams) => console.log('valueParses >>: ', params) : undefined,
    };
}

function getCellClassRules<TType extends GridRow = GridRow>(column: GridColumn, context: GridColumnHelperContext<TType>): ColDef['cellClassRules'] {
    return {
        'tt-grid__cell--save-pending': (params) => context.getCellSaveHighlightState?.(params.node?.id, column.colname) === 'pending',
        'tt-grid__cell--save-saved': (params) => context.getCellSaveHighlightState?.(params.node?.id, column.colname) === 'saved',
        'tt-grid__cell--edit-locked': (params) => isEditableScriptLocked(column, context, params as EditableCallbackParams),
    };
}

function getTooltipComponent(column: GridColumn): typeof Component | typeof GridHtmlTooltipComponent | undefined {
    if (column.tooltip_component?.trim()) {
        const tooltip = GridModalRegistry.get(column.tooltip_component);

        if (!tooltip) {
            console.error('Tooltip component not found, needs to registered by using the @GridModalComponent decorator, and added under app/modal/ routes to be impored.');
        } else {
            return tooltip;
        }
    } else if (!!column.tooltip_datatask || !!column.tooltip_script?.trim()) {
        return GridHtmlTooltipComponent;
    }

    return;
}

function setLockVisible<TType extends GridRow = GridRow>(column: GridColumn, context: GridColumnHelperContext<TType>): boolean {
    if (column.visibility?.toLocaleLowerCase().includes('lock')) {
        context.options.data!.hasLockVisibleColumn = true;
        return true;
    }
    return false;
}

/**
 * Creates the column defintion for the grid function column, a column used for displaying row buttons.
 *
 * @returns column definition for grid function column.
 */
function createGridFunctionsColumn<TType extends GridRow = GridRow>(context: GridColumnHelperContext<TType>): ColDef {
    context.options.data!.columnInfo!['grid_functions'] = { colname: 'grid_functions', title: context.translations['grid_functions'], remember: true, decimalWidth: null, type: 'alt' };

    return <ColDef>{
        colId: 'grid_functions',
        field: 'grid_functions',
        headerName: context.translations['grid_functions'],
        pinned: 'left',
        filter: false,
        floatingFilter: false,
        autoHeight: true,
        suppressAutoSize: true,
        suppressSizeToFit: true,
        lockPinned: true,
        cellRenderer: GridFunctionsCellRendererComponent,
        cellRendererParams: (params: ICellRendererParams) => <GridFunctionsCellRendererComponentParams<TType>>{ ...params, specialFunc: context.options.config?.specialFunc, grid: context.grid, options: context.options, openModalEventEmitter: context.ttOpenModal, navigateEventEmitter: context.ttNavigate, datataskEventEmitter: context.ttDatatask },
    };
}

/**
 * Creates the column definition for a symbol column.
 *
 * @returns column definition for a symbol column.
 */
function createSymbolColumn<TType extends GridRow = GridRow>(context: GridColumnHelperContext<TType>): ColDef {
    context.options.data!.columnInfo!['grid_glyphs'] = { colname: 'grid_glyphs', title: 'Symbol', remember: true, decimalWidth: null, type: 'icon' };

    return <ColDef>{
        colId: 'grid_glyphs',
        field: 'grid_glyphs',
        headerName: context.translations['grid_glyphs'],
        headerClass: 'symbol-header',
        width: 90,
        filter: false,
        wrapText: true,
        cellRenderer: SymbolCellRendererComponent,
        cellRendererParams: (params: ICellRendererParams) => <SymbolCellRendererParams>{ ...params, iconKeyname: 'item_glyphicon', iconColorKeyname: 'item_glyphicon_color' },
        suppressFloatingFilterButton: true,
        suppressHeaderMenuButton: true,
        editable: false,
        sortable: false,
    };
}

/**
 * Creates the column definition for a checkbox selection column.
 *
 * @returns column definition for a checkbox delection column.
 */
function createCheckboxSelectionColumn<TType extends GridRow = GridRow>(context: GridColumnHelperContext<TType>): ColDef {
    context.options.data!.columnInfo!['isselected'] = { colname: 'isselected', title: '', remember: true, decimalWidth: null, type: 'boolean' };

    return <ColDef>{
        colId: 'isselected',
        field: 'isselected',
        pinned: 'left',
        lockPinned: true,
        lockPosition: true,
        // lockVisible: true,
        hide: false,
        headerName: context.translations['is_selected'],
        headerClass: 'tt-checkbox-header',
        headerCheckboxSelection: context.rowSelection === 'multiple',
        checkboxSelection: true,
        showDisabledCheckboxes: true,
        floatingFilterComponent: false,
        suppressFloatingFilterButton: true,
        suppressMovable: true,
        type: 'centerAligned',
        width: 40,
    };
}

/**
 * Returns a list of css classes to apply the column header of the given column.
 *
 * @param column the column to configure headerclasses for.
 * @returns a list of css classes to apply the to given header column.
 */
function getHeaderClasses<TType extends GridRow = GridRow>(column: GridColumn, context: GridColumnHelperContext<TType>): string[] {
    const classes: string[] = [];

    if (context.options.data?.columnInfo?.[column.colname]?.clickonly === true) {
        classes.push('manual-edit-header');
    } else if (isEditableLookup(column.editable)) {
        classes.push('lookup-header');
    } else if (isEditable(column, context)) {
        classes.push('edit-header');
    }
    if (column.align === 'center') classes.push('tt-grid__cell--center');
    if (column.align === 'right' || column.coltype?.startsWith('N')) classes.push('tt-grid__cell--right');

    return classes;
}

/**
 * Returns a list of types to apply to the given column based on it'r properties.
 *
 * @param column the column to retrieve list of types for.
 * @returns a list of types to apply to the column.
 */
function getColumnTypes(column: GridColumn): string[] {
    const types: string[] = [];

    if (column.align === 'right') {
        types.push('rightAligned');
    } else if (column.align === 'center') {
        types.push('centerAligned');
    }

    if (column.coltype?.startsWith('N')) {
        types.push('numericColumn');
    }

    return types;
}

/**
 * Returns the appropriate styles based on the given column and parameters.
 * @param column
 * @param params
 * @returns
 */
function getColumnCellStyle<TType extends GridRow = GridRow>(column: GridColumn, params: any, context: GridColumnHelperContext<TType>): Partial<CSSStyleDeclaration> | undefined {
    try {
        if (!!column.style_script && !!params.data && !params.node.isRowPinned()) {
            return new Function('row', column.style_script).call(context.grid, params.data);
        }

        return undefined;
    } catch (error) {
        return undefined;
    }
}

function getCellClass<TType extends GridRow = GridRow>(column: GridColumn, params: any, context: GridColumnHelperContext<TType>): string[] {
    const classes: string[] = [];
    const buttonConfig = context.options.config!.specialFunc!.buttons?.find((button: any) => button?.name === column.colname);

    if (!!buttonConfig && (!buttonConfig.type || buttonConfig.type === 'cell')) {
        classes.push('tt-cell-goto');
    }

    if (column.align === 'right' || column.coltype?.startsWith('N')) {
        classes.push('tt-grid__cell--right');
    }

    if (column.align === 'center' || getCellDataType(column) === 'boolean') {
        classes.push('tt-grid__cell--center');
    }

    if (context.defaultColumnDefinitions.wrapText) {
        classes.push('tt-grid__cell__wrap-text');
    }

    if (isDateTimeColumn(column)) {
        classes.push('dateTimeType');
    } else if (isDateColumn(column)) {
        classes.push('dateType');
    } else if (isNumberColumn(column) && column.coltype === 'decimal') {
        let excelStyle = column.decimals + 'FormatNumberType';

        if (!context.excelStyles.some((style) => style.id === excelStyle)) {
            context.excelStyles.push({ id: excelStyle, dataType: 'Number', numberFormat: { format: '#,##0' + (column.decimals !== null && column.decimals > 0 ? '.' + '0'.repeat(column.decimals) : '') } });
        }

        classes.push(excelStyle);
    } else if (isNumberColumn(column)) {
        classes.push('numberType');
    } else if (getCellDataType(column) === 'boolean') {
        classes.push('booleanType');
    } else {
        classes.push('stringType');
    }
    return classes;
}

/**
 * Formats the cells to how they should be displayed in the grid, does not change the underlying value of the cell.
 *
 * @param params the value formatter parameters to format.
 * @returns a string used for displaying the value in the grid.
 */
export function formatGridColumnValue(params: ValueFormatterParams, column: GridColumn | null): string {
    if (column && isChipsColumn(column) && Array.isArray(params.value)) return '(' + params.value.reduce((acc, item) => acc + ', ' + item.item_name, '').replace(', ', '') + ')';

    const value = params.data?.[params.column.getColId()] ?? params.value;
    if (column === null) {
        const formatter = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: undefined, minimumFractionDigits: undefined });

        if (value !== undefined && value !== null && value !== '' && !isNaN(Number(value))) {
            return formatter.format(value).replaceAll('Â ', ' ').replaceAll('âˆ’', '-');
        }

        return value;
    }

    try {
        if (value === null || value === 'null') {
            return '';
        } else if (params.colDef.cellDataType === 'datetime' && value) {
            const date = getValidDate(value);

            return date ? formatDate(date, DATE_FORMATS.display.dateInput.format + ', HH:mm', DATE_FORMATS.display.dateInput.language || navigator.language || navigator.languages[0] || 'nb-NO') : value;
            // return Intl.DateTimeFormat(navigator?.language || navigator.languages[0] || 'nb-NO', { dateStyle: 'short', timeStyle: 'medium' }).format(new Date(value));
        } else if (params.colDef.cellDataType === 'date' && value) {
            if (params.value instanceof Date) {
                const date = getValidDate(value);

                return date ? formatDate(date, DATE_FORMATS.display.dateInput.format, DATE_FORMATS.display.dateInput.language || navigator.language || navigator.languages[0] || 'nb-NO') : '';
            } else {
                return value;
            }
            // return Intl.DateTimeFormat(navigator?.language || navigator.languages[0] || 'nb-NO', { dateStyle: 'short', timeStyle: undefined }).format(new Date(value));
        } else if (params.colDef.cellDataType === 'boolean') {
            return ['1', 1, true].includes(value).toString();
        } else if (column!.coltype === 'decimal') {
            const formatter = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: column!.decimals || undefined, minimumFractionDigits: column!.decimals || undefined });

            if (params.node?.group && params.node?.aggData && params.node.aggData[params.colDef.field!]) {
                return formatter.format(Number(params.node.aggData[params.colDef.field!]));
            } else if (value !== undefined && value !== null && value !== '' && !isNaN(Number(value))) {
                return formatter.format(value).replaceAll('Â ', ' ').replaceAll('âˆ’', '-');
            }
        }
    } catch (error) {
        return value;
    }

    return value;
}

function getValidDate(value: unknown): Date | null {
    const date = value instanceof Date ? value : new Date(value as string | number | Date);

    return date.toString() !== 'Invalid Date' ? date : null;
}

/**
 * Parses the values to native types.
 *
 * @param params value getter parameters to parse.
 * @returns
 */
function getGridColumnValue(params: ValueGetterParams, column: GridColumn): any {
    if (column.coltype === 'bit') {
        return ['1', 1, true].includes(params.data?.[params.column.getColId()]);
    }

    switch (params.colDef.cellDataType) {
        case 'boolean':
            return ['1', 1, true].includes(params.data?.[params.column.getColId()]);
        case 'number':
        case 'formatNumber':
            if (params.data?.[params.column.getColId()] || params.data?.[params.column.getColId()] === 0) {
                if (!isNaN(Number(params.data?.[params.column.getColId()]))) {
                    return parseFloat(params.data?.[params.column.getColId()]);
                }
                return params.data?.[params.column.getColId()];
            } else {
                return null;
            }
        case 'date':
            const dateString = params.data?.[params.column.getColId()];

            if (dateString && typeof dateString === 'string') {
                const dateParts = dateString.split('-');

                return dateParts.length === 3 ? new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2])) : null;
            } else if (dateString && dateString instanceof Date && dateString.toString() !== 'Invalid Date') {
                return dateString;
            } else {
                return null;
            }
        default:
            return params.data?.[params.column.getColId()];
    }
}

function getHeaderComponent<TType extends GridRow = GridRow>(column: GridColumn, context: GridColumnHelperContext<TType>): typeof CheckboxColumnHeaderComponent | undefined {
    if (isEditableCheckboxColumn(column, context)) {
        return CheckboxColumnHeaderComponent;
    }
    return undefined;
}

function getHeaderComponentParams<TType extends GridRow = GridRow>(params: IHeaderParams, column: GridColumn, context: GridColumnHelperContext<TType>): IHeaderParams | any {
    if (isEditableCheckboxColumn(column, context)) {
        return {
            ...params,
            ttGridOptions: context.options,
            isCellEditable: (node: IRowNode) => isHeaderCheckboxCellEditable(params, column, context, node),
            checkboxChanged: (key: string) => context.ttOptions.optionfunc?.({ data: { func: 'CheckboxHeaderClick', data: context.options.data?.rowData, key: key } }),
        };
    }

    return params;
}

function isHeaderCheckboxCellEditable<TType extends GridRow = GridRow>(params: IHeaderParams, column: GridColumn, context: GridColumnHelperContext<TType>, node: IRowNode): boolean {
    if (!node.data || node.group === true) return false;

    return isEditable(column, context, {
        api: params.api,
        column: params.column,
        colDef: params.column.getColDef(),
        context: (params as any).context,
        data: node.data,
        node,
    } as EditableCallbackParams);
}

/**
 * Gets the cell renderer for the given grid column. `null` uses default renderer.
 *
 * @param column the column to get cell renderer for.
 * @returns cell renderer for the given column.
 */
function getCellRenderer<TType extends GridRow = GridRow>(column: GridColumn, context: GridColumnHelperContext<TType>): any {
    const buttonConfig = context.options.config!.specialFunc!.buttons?.find((button: any) => button?.name === column.colname);

    if (isChipsColumn(column)) {
        return ChipCellRendererComponent;
    } else if (!!buttonConfig && (!buttonConfig.type || buttonConfig.type === 'cell')) {
        return LinkCellRendererComponent;
    } else if (column.coltype === 'bit') {
        return 'agCheckboxCellRenderer';
    } else if (column.colname === 'item_thumb') {
        return ThumbnailCellRendererComponent;
    }

    return undefined;
}

/**
 * Gets the cell renderer parameters for the cell renderer based on the given column.
 *
 * @param params the default parameters provided by ag agrid.
 * @param column the column to retrieve cell renderer parameters for.
 * @returns cell renderer parameters for the given column.
 */
function getCellRendererParams<TType extends GridRow = GridRow>(params: ICellRendererParams, column: GridColumn, context: GridColumnHelperContext<TType>): ICellRendererParams | LinkCellRendererParams | (ICellRendererParams & { grid: any }) {
    const buttonConfig = context.options.config!.specialFunc?.buttons?.find((button: any) => button?.name === column.colname);

    if (isChipsColumn(column)) {
        return { ...params, grid: context.grid };
    } else if (!!buttonConfig && (!buttonConfig.type || buttonConfig.type === 'cell')) {
        return <LinkCellRendererParams>{ ...params, button: buttonConfig, loadData: context.options.dataTask?.loadData, rememberId: context.options.dataTask?.rememberId, navigateEventEmitter: context.ttNavigate };
    }

    return params;
}

/**
 * Gets the appropriate cell editor component to use for the given column.
 *
 * @param column the column to get the cell editor for.
 * @returns a string or class of the component to use for cell editing.
 */
function getCellEditor(column: GridColumn): string | typeof DateTimeCellEditorComponent | typeof DateCellEditorComponent | typeof LookupMultiCellEditorComponent | typeof LookupCellEditorComponent | typeof NumberCellEditorComponent | null {
    // if (column.edittype === 'CB') {
    if (isCheckboxColumn(column)) {
        return 'agCheckboxCellEditor';
    } else if (column.coltype === 'date') {
        // return 'agDateCellEditor';
        return DateCellEditorComponent;
    } else if (column.coltype === 'datetime' || column.coltype === 'timestamp') {
        return DateTimeCellEditorComponent;
    } else if (isEditableDropdown(column.editable)) {
        return 'agRichSelectCellEditor';
    } else if (isEditableMultiLookup(column.editable) /* TODO: || column.colname.endsWith('_chips')*/) {
        return LookupMultiCellEditorComponent;
    } else if (isEditableLookup(column.editable)) {
        return LookupCellEditorComponent;
    } else if (column.edittype?.startsWith('N')) {
        return NumberCellEditorComponent;
    } else if (isLongTextColumn(column)) {
        return 'agLargeTextCellEditor';
    }

    return null;
}

/**
 * Gets cell editor parameters for the given grid column and basic cell editor parameter.
 *
 * @param column the grid column to get cell editor parameters for.
 * @param params the basic cell editor parameters from ag-grid.
 * @returns cell editor paramters.
 */
function getCellEditorParams<TType extends GridRow = GridRow>(column: GridColumn, params: ICellEditorParams, context: GridColumnHelperContext<TType>): ICellEditorParams | IRichCellEditorParams | LookupCellEditorParams | NumberCellEditorParams {
    const customCellEditorParams = {
        ...params,
        onCellEditorValueChanged: () => context.onCellEditorValueChanged?.(),
    };

    if (column.coltype === 'date' || column.coltype === 'datetime' || column.coltype === 'timestamp') {
        return customCellEditorParams;
    }

    if (isEditableDropdown(column.editable)) {
        const dropdownConfig = <DropdownConfig>column.editable;

        return <IRichCellEditorParams>{
            values: async (params) => {
                return (await context.datatask.Post(+dropdownConfig.lookup, { searchfield: column.colname, row: params.data })).map((val: any) => val[dropdownConfig.datatextfield || 'item_name']);
            },
            // parseValue: (value: any) => {
            // },
            formatValue: (value) => {
                if (typeof value === 'object') return value[dropdownConfig.datatextfield || 'item_name'];
                return '' + value;
            },
        };
    } else if (isEditableMultiLookup(column.editable) /* TODO: || column.colname.endsWith('_chips') */) {
        const lookupConfig = <LookupConfig>column.editable;

        return <LookupCellEditorParams<TType>>{
            ...customCellEditorParams,
            method: lookupConfig.lookup,
            field: column.colname,
            keyname: lookupConfig?.datatextfield || 'item_name',
            key: lookupConfig.key,
            relations: lookupConfig.relations,
            autoSelectFirst: lookupConfig.autoselectfirst,
            cancelOnTab: lookupConfig.cancelontab ?? false,
            options: context.options,
            onKeyDown: (event: KeyboardEvent) => context.onCellKeyDown(null, event, column.colname, params.node.rowIndex),
        };
    } else if (isEditableLookup(column.editable)) {
        const lookupConfig = <LookupConfig>column.editable;

        return <LookupCellEditorParams<TType>>{
            ...customCellEditorParams,
            method: lookupConfig.lookup,
            field: column.colname,
            keyname: lookupConfig?.datatextfield || 'item_name',
            key: lookupConfig.key,
            relations: lookupConfig.relations,
            autoSelectFirst: lookupConfig.autoselectfirst,
            cancelOnTab: lookupConfig.cancelontab ?? false,
            options: context.options,
            onKeyDown: (event: KeyboardEvent) => context.onCellKeyDown(null, event, column.colname, params.node.rowIndex),
        };
    } else if (column.edittype?.startsWith('N')) {
        return <NumberCellEditorParams>{
            ...customCellEditorParams,
            formatNumber: column.coltype === 'decimal',
            decimals: column.decimals,
            length: column.width,
        };
    } else if (isLongTextColumn(column)) {
        return <ILargeTextEditorParams>{
            ...params,
            maxLength: 524288,
        };
    }

    return params;
}

/**
 * Retrieves the floating filter type to use for the given grid column.
 *
 * @param column the grid column to find floating filter type for.
 * @returns the floating filter type to use for the given grid column.
 */
function getColumnFloatingFilterType(column: GridColumn): string | boolean | typeof CheckboxFloatingFilterComponent | typeof DateFloatingFilterComponent | typeof ChipFloatingFilterComponent | undefined {
    if (column.filterable === '0') return false;

    let filterType: string | boolean | typeof CheckboxFloatingFilterComponent | typeof DateFloatingFilterComponent | typeof ChipFloatingFilterComponent | undefined = true;

    if (isCheckboxColumn(column)) {
        filterType = CheckboxFloatingFilterComponent;
    } else if (isDateColumn(column) || isDateTimeColumn(column)) {
        filterType = undefined;
    } else if (column.coltype === 'null' || column.coltype === null) {
        filterType = false;
    } else if (isChipsColumn(column)) {
        filterType = false;
        // ChipFloatingFilterComponent;
    }

    return filterType;
}

/**
 * Retrieves the filter type for the given grid column to be used in multi-filter.
 *
 * @param column the grid column to find the main filter type for in a multi-filter.
 * @returns the filter type to use for the given grid column in a multi-filter.
 */
function getMultiFilterColumnFilter(column: GridColumn): string | boolean {
    if (column.filterable === '0') return false;

    let filterType: string | boolean = true;

    if (column.coltype?.includes('char') || isCheckboxColumn(column)) {
        filterType = 'agTextColumnFilter';
        // } else if (column.coltype === 'decimal' || column.coltype === 'integer' || column.coltype === 'numeric' || column.coltype === 'unsigned int') {
    } else if (isNumberColumn(column)) {
        filterType = 'agNumberColumnFilter';
    } else if (isDateColumn(column) || isDateTimeColumn(column)) {
        filterType = 'agDateColumnFilter';
    } else if (column.coltype === 'null' || column.coltype === null) {
        filterType = false;
    }

    return filterType;
}

function getMultiFilterColumnFilterParams(column: GridColumn): undefined | any {
    let filterParams: undefined | any;

    if (isDateTimeColumn(column)) {
        filterParams = {
            buttons: ['clear'],
            comparator: (filterLocalDateAtMidnight: Date, cellValue: string | Date) => {
                let date = !!cellValue && typeof cellValue === 'string' ? new Date(cellValue.substring(0, 10)) : cellValue instanceof Date ? new Date(cellValue.getTime()) : null;

                date?.setHours(filterLocalDateAtMidnight.getHours());
                date?.setMinutes(filterLocalDateAtMidnight.getMinutes());
                date?.setSeconds(filterLocalDateAtMidnight.getSeconds());
                date?.setMilliseconds(filterLocalDateAtMidnight.getMilliseconds());

                if (!date || date < filterLocalDateAtMidnight) {
                    return -1;
                } else if (date > filterLocalDateAtMidnight) {
                    return 1;
                }
                return 0;
            },
        };
    } else if (isNumberColumn(column)) {
        filterParams = <INumberFilterParams>{
            buttons: ['clear'],
            allowedCharPattern: '\\d\\-\\,\\$',
            numberParser: (text: string | null) => {
                return text == null ? null : parseFloat(text.replace(',', '.').replace('$', ''));
            },
            numberFormatter: (value: number | null) => {
                return value == null ? null : value.toString().replace('.', ',');
            },
        };
    } else {
        filterParams = {
            inRangeInclusive: true,
            buttons: ['clear'],
        };
    }

    return filterParams;
}

/**
 * Retrieves the floating filter component to be used in a multi filter type for the given grid-column.
 *
 * @param column the column to retrieve the floating filter component for used in a multi-filter.
 * @returns the floating filter component to use for the given column.
 */
function getMultiFilterFloatingFilter(column: GridColumn): string | undefined {
    if (column.filterable === '0') return;

    let filterType: string | undefined;

    if (isCheckboxColumn(column)) {
        filterType = undefined;
        // filterType = CheckboxFloatingFilterComponent;
        // filterType = false;
    } else if (isDateColumn(column) || isDateTimeColumn(column)) {
        filterType = 'agDateColumnFloatingFilter';
    } else if (column.coltype === 'null' || column.coltype === null) {
        filterType = undefined;
    }

    return filterType;
}

function getFilterComponent<TType extends GridRow = GridRow>(column: GridColumn, context: GridColumnHelperContext<TType>): string | boolean | typeof ChipFilterComponent {
    if (isCheckboxColumn(column)) {
        return 'agTextColumnFilter';
    } else if (isChipsColumn(column)) {
        return ChipFilterComponent;
    } else if (context.options.config?.serverSideHandling !== true) {
        return 'agMultiColumnFilter';
    } else {
        return getMultiFilterColumnFilter(column);
    }
}

function getFilterParams<TType extends GridRow = GridRow>(column: GridColumn, context: GridColumnHelperContext<TType>): undefined | IMultiFilterParams | any {
    if (isChipsColumn(column)) {
        return { grid: context.grid };
    } else if (isCheckboxColumn(column)) {
        return undefined;
        // } else if (this.isDateTimeColumn(column)) {
        //     return this.getMultiFilterFloatingFilter(column);
    } else if (context.options.config?.serverSideHandling !== true) {
        return {
            filters: [
                {
                    filter: getMultiFilterColumnFilter(column),
                    floatingFilterComponent: getMultiFilterFloatingFilter(column),
                    floatingFilterComponentParams: { buttons: ['clear'] },
                    filterParams: getMultiFilterColumnFilterParams(column),
                },
                {
                    filter: 'agSetColumnFilter',
                    floatingFilterComponent: 'agSetColumnFloatingFilter',
                    filterParams: <SetFilterParams>{
                        convertValuesToStrings: true,
                        defaultToNothingSelected: true,
                        buttons: ['clear'],
                        valueFormatter: (params: ValueFormatterParams) => formatGridColumnValue(params, column),
                    },
                },
            ],
        };
    } else {
        return;
    }
}

/**
 * Checks whether the given column is editable or not.
 *
 * @param column the column to check if is editable.
 * @returns `true` if the column is editable, `false` if not.
 */
function isEditable<TType extends GridRow = GridRow>(column: GridColumn, context: GridColumnHelperContext<TType>, event?: EditableCallbackParams): boolean {
    /* TODO: if (this.appSettings.settings.user?.portalUserKeynoEdit === 142 && column.colname.endsWith('_chips')) return true; */
    if (context.isEditingLocked?.() === true) return false;

    if (event && context.getGridApi()?.getPinnedBottomRow(0)?.data._uuid === event.node.data._uuid) return false;

    return isBaseEditable(column, context) && isEditableByScript(column, context, event) !== false;
}

function isEditableScriptLocked<TType extends GridRow = GridRow>(column: GridColumn, context: GridColumnHelperContext<TType>, event?: EditableCallbackParams): boolean {
    return !!column.editable_script?.trim() && isBaseEditable(column, context) && isEditableByScript(column, context, event) === false;
}

function isBaseEditable<TType extends GridRow = GridRow>(column: GridColumn, context: GridColumnHelperContext<TType>): boolean {
    let editable = false;
    const editColumn = context.options.config?.editColumns?.find((editCol) => editCol.key === column.colname);

    if (editColumn) {
        if (!!editColumn.clickonly || (typeof column.editable === 'object' && (column.editable as LookupConfig).clickonly)) {
            context.options.data!.columnInfo![column.colname].clickonly = editColumn.clickonly || (column.editable as LookupConfig).clickonly;
        }

        if (!!editColumn.lookup) {
            context.options.data!.columnInfo![column.colname].editable = editColumn.lookup;
            editable = true;
        } else {
            editable = true;
        }
    } else if (typeof column.editable === 'object' && column.editable?.clickonly === true) {
        context.options.data!.columnInfo![column.colname].clickonly = true;
        editable = true;
    } else {
        editable = isEditableLookup(column.editable) || column.editable === true;
    }

    return editable;
}

function isEditableByScript<TType extends GridRow = GridRow>(column: GridColumn, context: GridColumnHelperContext<TType>, event?: EditableCallbackParams): boolean | null {
    const script = column.editable_script?.trim();

    if (!script) return true;
    if (!event?.data || event.node?.isRowPinned()) return true;

    try {
        return getEditableScriptFunction(column, script).call(context.grid, event.data, column, event) === true;
    } catch (error) {
        console.error(error);
        return null;
    }
}

function getEditableScriptFunction(column: GridColumn, script: string): EditableScriptFunction {
    const cached = editableScriptCache.get(column);

    if (cached?.script === script) return cached.fn;

    const fn = new Function('row', 'column', 'params', script) as EditableScriptFunction;

    editableScriptCache.set(column, { script, fn });

    return fn;
}

/**
 * Checks if the given column is an editable checkbox column.
 * The predicate is that the column must be of boolean type and editable must be `true`, unless it is the `is_selected` column, where the default if editable `true` and thus will not be editable only if editable is `false`.
 *
 * @param column the column definition to check if is an editable checkbox column.
 * @returns `true` if the column is an editable checkbox column, `false` if not.
 */
function isEditableCheckboxColumn<TType extends GridRow = GridRow>(column: GridColumn, context: GridColumnHelperContext<TType>): boolean {
    return isCheckboxColumn(column) && (column.editable === true || (column.colname === 'is_selected' && column.editable !== false) || (!!context.ttOptions.config?.editColumns && context.ttOptions.config.editColumns?.some((item) => item.key === 'is_selected')));
}

export function isGridColumnFilterable<TType extends GridRow = GridRow>(options: GridOptions<TType>, column?: GridColumn): boolean {
    return ((!!column && column.filterable !== '0') || !column) && options.kendo?.filterable !== false;
}
